import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const STORAGE_KEY = "te_portal_token";
const PortalAuthContext = createContext(null);

// Axios instance scoped to the customer portal only (own interceptor so its
// 401s don't kick the admin out of their session, and vice-versa).
function buildClient() {
  const base = process.env.REACT_APP_BACKEND_URL;
  const client = axios.create({ baseURL: `${base}/api` });
  client.interceptors.request.use((cfg) => {
    const t = localStorage.getItem(STORAGE_KEY);
    if (t) cfg.headers.Authorization = `Bearer ${t}`;
    return cfg;
  });
  return client;
}

export const portalApi = buildClient();

export function PortalAuthProvider({ children }) {
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const t = localStorage.getItem(STORAGE_KEY);
    if (!t) {
      setCustomer(null);
      setLoading(false);
      return;
    }
    try {
      const r = await portalApi.get("/portal/auth/me");
      setCustomer(r.data);
    } catch {
      localStorage.removeItem(STORAGE_KEY);
      setCustomer(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    // Returns a TOTP challenge (setup or verify); never returns a JWT directly.
    const r = await portalApi.post("/portal/auth/login", { email, password });
    return r.data;
  }, []);

  const verifyTotp = useCallback(async (challenge_token, code) => {
    const r = await portalApi.post("/portal/auth/totp/verify", { challenge_token, code });
    localStorage.setItem(STORAGE_KEY, r.data.token);
    setCustomer(r.data.customer);
    return r.data.customer;
  }, []);

  const activate = useCallback(async (token, password) => {
    // Activation now returns a TOTP setup challenge instead of a JWT.
    const r = await portalApi.post("/portal/auth/activate", { token, password });
    return r.data;
  }, []);

  const resetTotp = useCallback(async () => {
    await portalApi.post("/portal/security/totp/reset", {});
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setCustomer(null);
  }, []);

  return (
    <PortalAuthContext.Provider value={{ customer, loading, login, verifyTotp, activate, resetTotp, logout, refresh }}>
      {children}
    </PortalAuthContext.Provider>
  );
}

export function usePortalAuth() {
  const ctx = useContext(PortalAuthContext);
  if (!ctx) throw new Error("usePortalAuth must be used inside PortalAuthProvider");
  return ctx;
}
